import {
  Component,
  signal,
  inject,
  input,
  output,
  effect,
  computed,
  ElementRef,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { RdsButtonModule, RdsIconsModule } from '@rds/angular-components';
import { Offering } from '../../../models/offering';
import { Router } from '@angular/router';
import { MenuCategory, MenuSection } from '../../../models/mega-menu';
import { FaroActionDirective } from '../../../directives/faro-action.directive';
import { SkeletonComponent } from '../../shared/skeleton/skeleton.component';

@Component({
  selector: 'inexp-mega-menu',
  imports: [
    RdsIconsModule,
    RdsButtonModule,
    SkeletonComponent,
    FaroActionDirective,
  ],
  templateUrl: './mega-menu.component.html',
  styleUrl: './mega-menu.component.scss',
  host: {
    '(document:click)': 'onOutsideClick($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MegaMenuComponent {
  private _router = inject(Router);

  menuCategories = input<MenuCategory[]>([]);
  menuSection = input<MenuSection>();
  isLoading = input<boolean>(false);
  closed = output<void>();

  selectedCategory = signal<MenuCategory | null>(null);

  /** Placeholder slots rendered while menu data loads (varied widths read naturally). */
  readonly skeletonCategoryWidths = ['82%', '64%', '76%', '70%', '85%', '60%'];
  readonly skeletonItems = Array.from({ length: 6 });

  sortedCategories = computed(() =>
    [...this.menuCategories()].sort((a, b) => a.category.localeCompare(b.category))
  );

  readonly seeAllBtn = viewChild<ElementRef>('seeAllBtnRef');
  readonly seeAllInBtn = viewChild<ElementRef>('seeAllInBtnRef');
  onOutsideClick(event: MouseEvent) {
    if (
      !(event.target as HTMLElement).closest('.mega-menu') &&
      !(event.target as HTMLElement).closest('.header-secondary-navigation')
    ) {
      this.closed.emit();
    }
  }

  autoSelectFirstCategory = effect(() => {
    const section = this.menuSection();
    const categories = this.menuCategories();

    if (section && categories.length > 0) {
      this.selectedCategory.set(this.sortedCategories()[0]);
      this.seeAllBtn()?.nativeElement.focus();
    } else {
      this.selectedCategory.set(null);
    }
  });

  onCategoryClicked(category: MenuCategory) {
    this.selectedCategory.set(category);
    this.seeAllInBtn()?.nativeElement.focus();
  }

  handleSeeAllBtn() {
    this.closed.emit();
    this._router.navigate(['/search'], { queryParams: { q: '' } });
  }

  handleSeeAllInBtn() {
    this.closed.emit();
    this._router.navigate(['/search'], {
      queryParams: { q: '', categories: this.selectedCategory()?.category },
    });
  }

  onItemClicked(item: Offering) {
    this.closed.emit();
    if (item.full_slug.length > 0) {
      this._router.navigate([item.full_slug]);
    }
  }

  onItemFocus(isLast: boolean) {
    if (isLast) {
      this.seeAllBtn()?.nativeElement.focus();
    }
  }
}
